import React, {useContext, useMemo} from 'react';
import PropTypes from 'prop-types';
import SMCLabel from '../SMCLabel';
import LayoutContext from '../../Layouts/LayoutContext';
import {hasValue} from '../../../utils/utils';
import { makeStyles } from '@mui/styles';
import clsx from "clsx";

/**
 * 
 * @param {Object} params
 * @param {node} params.label
 * @param {string} params.tooltip
 * @param {('start'|'end')} params.labelPosition
 * @param {string|number} params.labelWidth
 * @param {function} params.labelOnClick
 * @param {boolean} params.disabled
 * @param {boolean} params.inline
 * @param {string|number} params.width
 * @param {string|number} params.height
 * @param {boolean} params.required
 */
function SMCField({
    label,
    labelPosition='start',
    tooltip,
    height,
    labelWidth,
    labelOnClick,
    width,
    disabled,
    inline=false,
    required=false,
    children,
    vertical,
    className
}) {
    const layoutContext = useContext(LayoutContext);
    const classes = useStyles();

    // Label is first if label position is not end, every other value defaults to label being first
    const labelFirst = labelPosition !== 'end';

    const _labelWidth = useMemo(() => {
        if (hasValue(labelWidth)) {
            return labelWidth;
        }
        else if (labelFirst) {
            if (layoutContext && layoutContext.labelWidth) {
                return layoutContext.labelWidth;
            }
        }
        return undefined;
    }, [labelWidth, labelFirst, layoutContext]);

    // Set right padding on label if label is first and width is undefined, so that they don't touch
    const labelPaddingRight = (labelFirst && typeof _labelWidth === 'undefined' ) ? '7px' : '0px';

    const _label = hasValue(label) ? (
        <SMCLabel
            disabled={disabled}
            width={_labelWidth}
            tooltip={tooltip}
            onClick={labelOnClick}
            style={{ paddingRight: labelPaddingRight }}
            inline
        >
            {label}{required && <span className={classes.requiredAsterisk}>*</span>}
        </SMCLabel>
    ) : null;

    return (
        <div className={vertical ? clsx(className, classes.root, classes.vertical) : clsx(className, classes.root)}
             style={{width, height, display: vertical ? null : inline ? 'inline-flex' : 'flex'}}>
            <>
                {
                    labelFirst ? _label : children
                }
                {
                    labelFirst ? children : _label
                }
            </>
        </div>
    );
}

const useStyles = makeStyles({
    root: {
        paddingTop: '3px',
        paddingBottom: '3px',
        alignItems: 'center',
    },
    requiredAsterisk: {
        color: 'red',
        paddingLeft: '1px',
        fontSize: '10px',
        position: 'relative',
        top: '-2px'
    },
    vertical:{
        display:"inline-grid",
        lineHeight: "22 px",
    }
})

SMCField.propTypes = {
    label: PropTypes.node,
    tooltip: PropTypes.node,
    disabled: PropTypes.bool,
    inline: PropTypes.bool,
    labelPosition: PropTypes.oneOf(['start', 'end']),
    labelWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    labelOnClick: PropTypes.func,
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    required: PropTypes.bool,
    children: PropTypes.node.isRequired
}

// SMCField.whyDidYouRender = true;

export default React.memo(SMCField);